import type { APITeoMarker, MarkerGroupName } from '@/teoCore/TeoApi';
import type { TeoText } from '@/teoCore/text/TeoText';

import { markerColors } from './config';
import { EditorMarkings } from './EditorMarkings.svelte';
import type EditorContent from './lexical/EditorContent';

export type EditorMarkingStyle = 'background' | 'underline' | 'block';

export interface TeoMarker {
  start: number;
  end: number;
  type: string;
  color: string;
  hoverColor?: string;
  underlineColor?: string;
  style: EditorMarkingStyle;
  id: number;
  isHighlighted?: boolean;
  rawMarker: APITeoMarker;
  markerGroup: MarkerGroupName;
  icon?: boolean;
}

export class EditorMarkerManager {
  markerList: TeoMarker[] = [];
  private sizeChangeTimeout: number = 0;
  private _editorContent: EditorContent | null = null;
  private listenerRemovers: (() => void)[] = [];
  // TODO: better id handling, prevent collisions with relation ids
  private idCounter = 10000;
  markings: EditorMarkings;
  private currentlyShownMarkers: string[] = [];
  private markerResetQueue: Promise<void> = Promise.resolve();
  private pendingResets = 0;

  constructor(
    private text: TeoText,
    container: HTMLElement,
  ) {
    this.markings = new EditorMarkings(this, container);
    this.listenerRemovers.push(
      text.events.addEventListener('gotAnalysis', (result) => {
        this.resetMarkers(result.getMarkers(), 'analysis');
      }),
      text.wordLists.events.addEventListener('scanPending', (pending) => {
        if (pending) this.resetMarkers([], 'wordlist');
      }),
      text.wordLists.events.addEventListener('gotScanResult', () => {
        this.resetMarkers(text.wordLists.markers, 'wordlist');
      }),
      text.spellCheck.events.addEventListener('spellCheckReceived', (detail) => {
        this.resetMarkers(detail.markers, 'spellcheck');
      }),
      text.diff.events.addEventListener('markersUpdated', (markers) => {
        this.resetMarkers(markers, 'diff');
      }),
      text.ui.events.addEventListener('markers', (markers) => {
        this.showMarkers(markers);
      }),
      text.markers.events.addEventListener('markerReplacement', (event) => {
        this.replaceMarkerContent(event.markerId, event.newContent);
        this.markings.addEditedMarker(event.markerId);
      }),
      text.markers.events.addEventListener('markersFocused', (event) => {
        this.markings.setFocusedMarkers(event.markerIds);
      }),
    );
  }

  get editorContent(): EditorContent | null {
    return this._editorContent;
  }

  set editorContent(editorContent: EditorContent) {
    this._editorContent = editorContent;
    this.listenerRemovers.push(
      this._editorContent.events.addEventListener('contentChanged', () => {
        this.markings.reRender();
      }),
    );
    if (this.text.analysis) {
      this.resetMarkers(this.text.analysis.getMarkers(), 'analysis');
    }
    if (this.text.wordLists.markers) {
      this.resetMarkers(this.text.wordLists.markers, 'wordlist');
    }
    if (this.text.spellCheck.markers) {
      this.resetMarkers(this.text.spellCheck.markers, 'spellcheck');
    }
  }

  getMarkerById(markerId: number): TeoMarker | null {
    return this.markerList.find((marker) => marker.id === markerId) || null;
  }

  addMarkers(markers: TeoMarker[], onUpdate?: () => void): void {
    this.markerList = this.markerList.concat(markers);
    this._editorContent?.addMarkers(markers, () => {
      if (this.text.ui.state.markers.length > 0) {
        // this._editorContent?.hideMarkers();
        this.markings.hideMarkers();
        this.showMarkers(this.text.ui.state.markers);
      }
      if (onUpdate) {
        onUpdate();
      }
    });
  }

  showMarkers(markerNames: string[]): void {
    const markers = this.markerList.filter((marker) => markerNames.includes(marker.type));
    this.markings.showMarkers(markers, this.text.markers.clickableMarkers);
    this.currentlyShownMarkers = markerNames;
  }

  hideMarkers(): void {
    this.markings.hideMarkers();
    this.currentlyShownMarkers = [];
  }

  clearMarkers(groupName?: MarkerGroupName): void {
    if (groupName) {
      const markerIds: number[] = [];
      this.markerList = this.markerList.filter((marker) => {
        if (marker.markerGroup === groupName) {
          markerIds.push(marker.id);
          return false;
        }
        return true;
      });
      if (markerIds.length !== 0) {
        this._editorContent?.clearMarkers(markerIds);
      }
    } else {
      this.markerList = [];
      this._editorContent?.clearMarkers();
    }
  }

  sizeChangeUpdate() {
    if (this.sizeChangeTimeout !== 0) {
      window.clearTimeout(this.sizeChangeTimeout);
    }
    this.sizeChangeTimeout = window.setTimeout(() => {
      this.markings.reRender();
    }, 50);
  }

  private resetMarkers(
    markers: APITeoMarker[],
    markerGroup: MarkerGroupName,
    onUpdate?: () => void,
  ): void {
    this.pendingResets++;
    this.markerResetQueue = this.markerResetQueue
      .then(
        () =>
          new Promise<void>((resolve) => {
            console.log('Resetting markers for group', markerGroup, markers);
            this.markings.clearEditedMarkers();
            this.hideMarkers();
            this.clearMarkers(markerGroup);
            this.addMarkers(this.createTeoMarkers(markers, markerGroup), () => {
              if (onUpdate) onUpdate();
              resolve(); // Only resolve when addMarkers' onUpdate is called
            });
          }),
      )
      .catch((err) => {
        console.error('Error in resetMarkers queue:', err);
      });
  }

  clickMarker(markerId: number) {
    const marker = this.markerList.find((marker) => marker.id === markerId);
    if (!marker) {
      console.error('Marker not found');
      return;
    }
    this.text.markers.clickMarker(markerId, marker.rawMarker);
  }

  replaceMarkerContent(markerId: number, content: string) {
    this.editorContent?.replaceMarkerContent(markerId, content);
  }

  private createTeoMarkers(apiMarkers: APITeoMarker[], markerGroup: MarkerGroupName): TeoMarker[] {
    return apiMarkers
      .map((marker) => {
        if (marker.length === 0) {
          return null;
        }
        return this.createTeoMarker(marker, markerGroup);
      })
      .filter((marker): marker is TeoMarker => marker !== null);
  }

  private createTeoMarker(apiMarker: APITeoMarker, markerGroup: MarkerGroupName): TeoMarker {
    if (apiMarker.marker && apiMarker.marker.startsWith('paragraph')) {
      return this.createParagraphTeoMarker(apiMarker, markerGroup);
    } else if (apiMarker.marker && apiMarker.marker == 'sentence') {
      return this.createSentenceTeoMarker(apiMarker, markerGroup);
    } else if (apiMarker.groupName === 'wordlist' || apiMarker.groupName === 'spellcheck') {
      return this.createReplaceableTeoMarker(apiMarker, markerGroup);
    } else if (apiMarker.marker === 'editor_diff') {
      return this.createDiffTeoMarker(apiMarker, markerGroup);
    }
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: apiMarker.marker,
      color: markerColors[apiMarker.marker] || 'rgba(255,100,100,0.5)',
      id: apiMarker.relation ?? this.idCounter++,
      style: 'background',
      markerGroup,
      rawMarker: apiMarker,
    };
  }

  private createDiffTeoMarker(apiMarker: APITeoMarker, markerGroup: MarkerGroupName): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: apiMarker.marker,
      color: markerColors[apiMarker.marker] || 'rgba(255,100,100,0.5)',
      id: apiMarker.relation ?? this.idCounter++,
      style: 'underline',
      markerGroup,
      rawMarker: apiMarker,
    };
  }

  private createParagraphTeoMarker(
    apiMarker: APITeoMarker,
    markerGroup: MarkerGroupName,
  ): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: 'paragraph',
      color: markerColors['paragraph'] || 'rgba(255,100,100,0.5)',
      hoverColor: markerColors['paragraph-hover'],
      id: apiMarker.relation ?? this.idCounter++,
      style: 'block',
      markerGroup,
      rawMarker: apiMarker,
      icon: true,
    };
  }

  private createSentenceTeoMarker(
    apiMarker: APITeoMarker,
    markerGroup: MarkerGroupName,
  ): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: 'sentence',
      color: markerColors['paragraph'] || 'rgba(255,100,100,0.5)',
      hoverColor: markerColors['paragraph-hover'],
      id: apiMarker.relation ?? this.idCounter++,
      style: 'background',
      markerGroup,
      rawMarker: apiMarker,
      icon: true,
    };
  }

  private createReplaceableTeoMarker(
    apiMarker: APITeoMarker,
    markerGroup: MarkerGroupName,
  ): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: apiMarker.marker,
      color: (apiMarker.color ? markerColors[apiMarker.color] : false) || 'rgba(100,255,100,0.5)',
      underlineColor: apiMarker.color ? markerColors['s' + apiMarker.color] : undefined,
      id: apiMarker.relation ?? this.idCounter++,
      style: 'underline',
      markerGroup,
      rawMarker: apiMarker,
    };
  }

  destroy() {
    this.listenerRemovers.forEach((remove) => remove());
  }
}
